import _debounce from 'lodash.debounce';
import { writeSavedScrollPosition } from './scrollPositionStorage';

const LAYOUT_STABLE_MS = 500;
const LAYOUT_POLL_MS = 100;
const MAX_RESTORE_DURATION_MS = 15_000;
const SCROLL_POSITION_TOLERANCE_PX = 2;

type ScrollRestoreParams = {
  container: HTMLElement;
  storageKey: string;
  savedScrollTop: number;
  allowScrollSave: { current: boolean };
};

type ScrollRestoreSession = {
  cancelled: boolean;
  restored: boolean;
  lastScrollHeight: number;
};

type RestoreTimers = {
  maxDurationTimeout: ReturnType<typeof setTimeout> | null;
  pollInterval: ReturnType<typeof setInterval> | null;
  releaseSaveGuardsFrame: number | null;
};

type ScrollRestoreContext = {
  params: ScrollRestoreParams;
  session: ScrollRestoreSession;
  timers: RestoreTimers;
  previousOverflowAnchor: string;
  debouncedStabilityRestore: ReturnType<typeof _debounce>;
  resizeObserver: ResizeObserver;
};

function getMaxScrollTop(container: HTMLElement): number {
  return Math.max(0, container.scrollHeight - container.clientHeight);
}

function scrollToTarget(container: HTMLElement, scrollTop: number) {
  container.scrollTo({ top: Math.min(scrollTop, getMaxScrollTop(container)) });
}

function contentCanReachTarget(
  container: HTMLElement,
  scrollTop: number,
): boolean {
  return getMaxScrollTop(container) >= scrollTop - SCROLL_POSITION_TOLERANCE_PX;
}

function createSession(container: HTMLElement): ScrollRestoreSession {
  return {
    cancelled: false,
    restored: false,
    lastScrollHeight: container.scrollHeight,
  };
}

function createEmptyTimers(): RestoreTimers {
  return {
    maxDurationTimeout: null,
    pollInterval: null,
    releaseSaveGuardsFrame: null,
  };
}

// guards against restoring scroll if session is not cancelled,
// finished, or the content is not tall enough to scroll to
function canApplyScrollRestore(
  session: ScrollRestoreSession,
  container: HTMLElement,
  savedScrollTop: number,
): boolean {
  if (
    session.cancelled ||
    session.restored ||
    !contentCanReachTarget(container, savedScrollTop)
  ) {
    return false;
  }
  return true;
}

// stopMonitoring tears down scroll timers and observers
function stopMonitoring(ctx: ScrollRestoreContext) {
  ctx.debouncedStabilityRestore.cancel();
  if (ctx.timers.maxDurationTimeout) {
    clearTimeout(ctx.timers.maxDurationTimeout);
    ctx.timers.maxDurationTimeout = null;
  }
  if (ctx.timers.pollInterval) {
    clearInterval(ctx.timers.pollInterval);
    ctx.timers.pollInterval = null;
  }
  ctx.resizeObserver.disconnect();
}

// finishRestore marks the session scroll as restored, persists the
// scroll position, and reenables saving the scroll position
function finishRestore(ctx: ScrollRestoreContext) {
  const { container, storageKey, savedScrollTop, allowScrollSave } = ctx.params;
  const { session } = ctx;

  if (session.cancelled || session.restored) {
    return;
  }
  session.restored = true;
  stopMonitoring(ctx);
  container.style.overflowAnchor = ctx.previousOverflowAnchor; // restore after layout stabilizes

  if (canApplyScrollRestore(session, container, savedScrollTop)) {
    writeSavedScrollPosition(storageKey, container.scrollTop);
  }

  // Re-enable saving after paint so scroll listeners don't persist a mid-restore position
  ctx.timers.releaseSaveGuardsFrame = requestAnimationFrame(() => {
    ctx.timers.releaseSaveGuardsFrame = null;
    if (!session.cancelled) {
      allowScrollSave.current = true;
    }
  });
}

// handleLayoutChange reacts to scrollHeight changes
function handleLayoutChange(ctx: ScrollRestoreContext) {
  const { container, savedScrollTop } = ctx.params;
  const { session } = ctx;
  const { scrollHeight } = container;

  if (
    session.cancelled ||
    session.restored ||
    scrollHeight === session.lastScrollHeight
  ) {
    return;
  }

  session.lastScrollHeight = scrollHeight;
  if (canApplyScrollRestore(session, container, savedScrollTop)) {
    scrollToTarget(container, savedScrollTop);
  }
  ctx.debouncedStabilityRestore();
}

function createDebouncedStabilityRestore(ctx: ScrollRestoreContext) {
  return _debounce(() => {
    const { session } = ctx;
    if (!session.cancelled && !session.restored) {
      scrollToTarget(ctx.params.container, ctx.params.savedScrollTop);
      finishRestore(ctx);
    }
  }, LAYOUT_STABLE_MS);
}

function observeLayoutChanges(ctx: ScrollRestoreContext) {
  const { container } = ctx.params;

  ctx.resizeObserver = new ResizeObserver(() => handleLayoutChange(ctx));
  ctx.resizeObserver.observe(container);

  const content = container.firstElementChild;
  if (content instanceof HTMLElement) {
    ctx.resizeObserver.observe(content);
  }

  ctx.timers.pollInterval = setInterval(
    () => handleLayoutChange(ctx),
    LAYOUT_POLL_MS,
  );
}

function applyInitialScroll(ctx: ScrollRestoreContext) {
  const { container, savedScrollTop } = ctx.params;
  const { session } = ctx;

  if (canApplyScrollRestore(session, container, savedScrollTop)) {
    scrollToTarget(container, savedScrollTop);
  }
  ctx.debouncedStabilityRestore();
  ctx.timers.maxDurationTimeout = setTimeout(
    () => finishRestore(ctx),
    MAX_RESTORE_DURATION_MS,
  );
}

function cleanupRestore(ctx: ScrollRestoreContext) {
  const { container, allowScrollSave } = ctx.params;
  const { session } = ctx;

  session.cancelled = true;
  allowScrollSave.current = session.restored;
  stopMonitoring(ctx);
  if (ctx.timers.releaseSaveGuardsFrame != null) {
    cancelAnimationFrame(ctx.timers.releaseSaveGuardsFrame);
  }
  container.style.overflowAnchor = ctx.previousOverflowAnchor; // restore on early teardown
}

/**
 * Re-applies a saved scroll offset on every layout change until scrollHeight
 * stabilizes, then re-enables scroll persistence. Returns a cleanup function.
 */
export function startScrollPositionRestore(
  params: ScrollRestoreParams,
): () => void {
  const { container } = params;
  const previousOverflowAnchor = container.style.overflowAnchor; // preserve inline style
  container.style.overflowAnchor = 'none'; // avoid browser fighting programmatic scroll

  const ctx = {
    params,
    session: createSession(container),
    timers: createEmptyTimers(),
    previousOverflowAnchor,
  } as ScrollRestoreContext;

  ctx.debouncedStabilityRestore = createDebouncedStabilityRestore(ctx);
  observeLayoutChanges(ctx);
  applyInitialScroll(ctx);

  return () => cleanupRestore(ctx);
}
